import { useState, useRef, useEffect, useCallback } from 'react';

const DEFAULT_SNAP_HEIGHTS = [35, 60, 85]; // vh
const CLOSE_THRESHOLD = 20; // vh

export function useResizableSheet(onClose, defaultSnapIndex = 1, snapHeights = DEFAULT_SNAP_HEIGHTS) {
  const SNAP_HEIGHTS = snapHeights;
  const [heightVh, setHeightVh] = useState(SNAP_HEIGHTS[defaultSnapIndex]);
  const [isDragging, setIsDragging] = useState(false);
  // Callback ref — zmiana elementu w DOM triggeruje useEffect
  const [handleEl, setHandleEl] = useState(null);
  const handleRef = useCallback((el) => setHandleEl(el), []);

  const dragStartY = useRef(0);
  const dragStartHeight = useRef(SNAP_HEIGHTS[defaultSnapIndex]);
  const currentHeightRef = useRef(SNAP_HEIGHTS[defaultSnapIndex]);

  const snapToNearest = useCallback((vh) => {
    if (vh < CLOSE_THRESHOLD) {
      onClose();
      const h = SNAP_HEIGHTS[defaultSnapIndex];
      setHeightVh(h);
      currentHeightRef.current = h;
      return;
    }
    const nearest = SNAP_HEIGHTS.reduce((prev, curr) =>
      Math.abs(curr - vh) < Math.abs(prev - vh) ? curr : prev
    );
    setHeightVh(nearest);
    currentHeightRef.current = nearest;
  }, [onClose, defaultSnapIndex]);

  useEffect(() => {
    if (!handleEl) return;

    const onTouchStart = (e) => {
      dragStartY.current = e.touches[0].clientY;
      dragStartHeight.current = currentHeightRef.current;
      setIsDragging(true);
    };

    const onTouchMove = (e) => {
      e.preventDefault();
      const deltaY = dragStartY.current - e.touches[0].clientY;
      const deltaVh = (deltaY / window.innerHeight) * 100;
      const newHeight = Math.min(90, Math.max(10, dragStartHeight.current + deltaVh));
      setHeightVh(newHeight);
      currentHeightRef.current = newHeight;
    };

    const onTouchEnd = () => {
      setIsDragging(false);
      snapToNearest(currentHeightRef.current);
    };

    handleEl.addEventListener('touchstart', onTouchStart, { passive: true });
    handleEl.addEventListener('touchmove', onTouchMove, { passive: false });
    handleEl.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      handleEl.removeEventListener('touchstart', onTouchStart);
      handleEl.removeEventListener('touchmove', onTouchMove);
      handleEl.removeEventListener('touchend', onTouchEnd);
    };
  }, [handleEl, snapToNearest]);

  const resetHeight = useCallback(() => {
    const h = SNAP_HEIGHTS[defaultSnapIndex];
    setHeightVh(h);
    currentHeightRef.current = h;
  }, [defaultSnapIndex]);

  const sheetStyle = {
    height: `${heightVh}vh`,
    transition: isDragging ? 'none' : 'height 0.3s cubic-bezier(0.25, 0.8, 0.5, 1)',
  };

  return { handleRef, sheetStyle, resetHeight };
}
